import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Rocket, ShieldCheck, Database, Activity, Lock, GitBranch,
  Sparkles, FileText, Layers, Wrench, Calendar, LifeBuoy,
} from 'lucide-react';
import StatsBand from './ui/StatsBand';
import Faq from './ui/Faq';
import CtaBand from './ui/CtaBand';
import HowItWorks from './ui/HowItWorks';
import Seo from './ui/Seo';

const STATS = [
  { num: '2–8 weeks', label: 'Typical time to launch' },
  { num: 'Fixed-fee', label: 'After a paid discovery' },
  { num: 'Auth & infra', label: 'Included by default' },
  { num: 'Run for you', label: 'Optional ongoing support' },
];

const FOR_WHO = [
  {
    Icon: Sparkles,
    title: 'You vibe-coded a prototype',
    text:
      'You shipped a v0 / Lovable / Cursor / Bolt app that demos your vision — but it would melt the moment a real user touched it.',
  },
  {
    Icon: FileText,
    title: 'You have a detailed spec',
    text:
      'No code yet — just a clear product idea, a doc, or a Figma. We translate that into a production app.',
  },
  {
    Icon: Layers,
    title: 'You\'re a non-technical founder',
    text:
      'You don\'t want to hire engineers, manage a cloud account, or learn what auth is. You want a real, working product.',
  },
  {
    Icon: Wrench,
    title: 'You\'re an internal team',
    text:
      'You need a custom tool fast, without going through procurement, hiring, or a six-month enterprise build.',
  },
];

const PROD_PATTERNS = [
  {
    Icon: ShieldCheck,
    title: 'Real authentication',
    text:
      'Not a hardcoded password. Real users, real roles, real teams, real password resets — wired into the CoPlaid platform.',
  },
  {
    Icon: Database,
    title: 'Data that survives',
    text:
      'A real database with backups, migrations, and the discipline that keeps your data correct as the product evolves.',
  },
  {
    Icon: Activity,
    title: 'Observability built in',
    text:
      'Logging, metrics, and alerting from day one — so when something goes sideways, we (and you) know before customers do.',
  },
  {
    Icon: Lock,
    title: 'Security as a default',
    text:
      'Encrypted transport, least-privilege access, audit logging, and secrets handled properly. The boring parts that matter.',
  },
  {
    Icon: GitBranch,
    title: 'Real deploys',
    text:
      'Version control, staging environment, repeatable production deploys. We hand you a project that any engineer can pick up.',
  },
  {
    Icon: LifeBuoy,
    title: 'Run mode (optional)',
    text:
      'We can keep operating the app for you — uptime, updates, support — on a predictable monthly fee.',
  },
];

const SHIP_STEPS = [
  {
    Icon: FileText,
    title: 'Intake',
    text: 'Send us your prototype or a doc. We do a paid 1–2 day deep-dive and come back with a fixed-fee proposal.',
  },
  {
    Icon: Layers,
    title: 'Architecture',
    text: 'We translate your idea into a production blueprint: data model, auth, integrations, and how it fits CoPlaid.',
  },
  {
    Icon: Rocket,
    title: 'Build',
    text: 'We rebuild the app properly — real code, real tests, real review. Weekly demos so you see it come together.',
  },
  {
    Icon: Calendar,
    title: 'Launch',
    text: 'Deployed on CoPlaid. We migrate any existing data, run a soft launch, then flip it on for real users.',
  },
  {
    Icon: LifeBuoy,
    title: 'Run',
    text: 'You can hand off to your team or keep us on retainer for maintenance, support, and ongoing changes.',
  },
];

const FAQS = [
  {
    q: 'Who owns the code?',
    a: 'You do. We use standard tools, push code to a repo in your account, and document everything. You\'re never locked in — if you ever want to bring it in-house, you can.',
  },
  {
    q: 'How long does a typical build take?',
    a: 'Most projects launch in 2–8 weeks depending on scope. Tiny internal tools can be live in a week; multi-tenant SaaS with payments and integrations takes longer. We commit to a timeline after the discovery.',
  },
  {
    q: 'How much does it cost?',
    a: 'Pricing has two parts: a fixed-fee implementation (set after a paid discovery, so it doesn\'t change mid-project) and a monthly platform + support fee. We don\'t bill hourly and we don\'t do open-ended scopes.',
  },
  {
    q: 'What if I bring a vibe-coded prototype?',
    a: 'Even better. The prototype tells us exactly what you want — we replace the parts that won\'t survive production (auth, data, deploys, error handling) and keep the parts that capture your product vision.',
  },
  {
    q: 'What if I just have a spec or a Figma?',
    a: 'Also fine. We can go from a written requirement to a working app. We just spend a bit more time in the architecture phase clarifying what "done" means.',
  },
  {
    q: 'Can I hire developers and bring this in-house later?',
    a: 'Yes. Everything we build is standard, documented, and yours. You can run it on CoPlaid forever, hire engineers to take it over, or migrate it elsewhere. Your choice.',
  },
  {
    q: 'Do you only build on CoPlaid?',
    a: 'For Ship engagements, yes — CoPlaid handles the auth and infrastructure so we can focus on your product. If you need something we don\'t support, we will tell you up front during the discovery.',
  },
  {
    q: 'How do you handle data privacy and compliance?',
    a: 'Standard encrypted transport, least-privilege access, audit logging. For HIPAA / SOC2 / regulated workloads, we scope explicitly during discovery — not every engagement is a fit, and we will be honest about it.',
  },
];

const Ship = () => {
  const navigate = useNavigate();
  return (
    <div>
      <Seo
        title="Ship"
        path="/ship"
        description="From prototype to production. We take your vibe-coded app or your spec and rebuild it as a real production app on CoPlaid."
      />

      {/* HERO */}
      <section className="cp-hero">
        <div className="cp-container cp-hero-grid">
          <div>
            <span className="cp-eyebrow">
              <Rocket size={12} style={{ verticalAlign: '-1px', marginRight: 6 }} />
              Ship · Vibe code → production
            </span>
            <h1 className="cp-h1">
              From prototype to production. Shipped.
            </h1>
            <p className="cp-lede">
              You vibe-coded an app to show what you mean. We rebuild it production-grade
              on CoPlaid — auth, hosting, scale all handled — so real users can actually
              use it.
            </p>
            <div className="cp-hero-actions">
              <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate('/contact')}>
                Book a build call <ArrowRight size={18} />
              </button>
              <button className="cp-btn cp-btn--ghost cp-btn--lg" onClick={() => navigate('/contact')}>
                Send us your prototype
              </button>
            </div>
            <div className="cp-hero-meta">
              <span><Calendar size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /><strong>2–8 weeks</strong> to launch</span>
              <span><ShieldCheck size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /><strong>Fixed-fee</strong> after discovery</span>
            </div>
          </div>

          {/* Visual: prototype → production transformation */}
          <div className="cp-screenshot cp-screenshot--ink" style={{ padding: 28 }}>
            <div style={{ display: 'grid', gap: 14, fontFamily: 'var(--font-sans)', color: '#fff' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                Your prototype
              </div>
              <div style={{ padding: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>my-vibe-app.lovable.app</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', borderRadius: 999 }}>localStorage auth</span>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', borderRadius: 999 }}>no database</span>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(245,158,11,0.15)', color: '#fbbf24', borderRadius: 999 }}>melts at 5 users</span>
                </div>
              </div>

              <div style={{ textAlign: 'center', color: 'var(--brand-300)', padding: '4px 0' }}>
                <Rocket size={20} />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                After Ship
              </div>
              <div style={{ padding: 14, background: 'rgba(102, 101, 221, 0.12)', border: '1px solid rgba(102, 101, 221, 0.35)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: '#fff' }}>app.yourcompany.com</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(16, 185, 129, 0.18)', color: '#34d399', borderRadius: 999 }}>real auth</span>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(16, 185, 129, 0.18)', color: '#34d399', borderRadius: 999 }}>real database</span>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(16, 185, 129, 0.18)', color: '#34d399', borderRadius: 999 }}>scales to 50k+</span>
                  <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(16, 185, 129, 0.18)', color: '#34d399', borderRadius: 999 }}>monitored</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="cp-section--sm">
        <div className="cp-container">
          <StatsBand items={STATS} />
        </div>
      </section>

      {/* Who it's for */}
      <section className="cp-section">
        <div className="cp-container">
          <div style={{ maxWidth: 720, marginBottom: 48 }}>
            <span className="cp-eyebrow">Who Ship is for</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>If any of these sound like you, we should talk.</h2>
          </div>
          <div className="cp-grid-2">
            {FOR_WHO.map(({ Icon, title, text }) => (
              <div className="cp-card" key={title}>
                <div className="cp-card-icon"><Icon size={22} /></div>
                <h3 className="cp-card-title">{title}</h3>
                <p className="cp-card-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we add (the gap between prototype and production) */}
      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <div style={{ maxWidth: 720, marginBottom: 48 }}>
            <span className="cp-eyebrow">What "production" actually means</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>The boring parts your prototype is missing — and why they matter.</h2>
            <p className="cp-lede">
              Vibe-coded apps look right and demo great. They fall over the moment a second
              user logs in, a record needs to survive a deploy, or someone tries something
              you didn't anticipate. Here's what we add.
            </p>
          </div>
          <div className="cp-grid-3">
            {PROD_PATTERNS.slice(0, 3).map(({ Icon, title, text }) => (
              <div className="cp-card" key={title}>
                <div className="cp-card-icon"><Icon size={22} /></div>
                <h3 className="cp-card-title">{title}</h3>
                <p className="cp-card-text">{text}</p>
              </div>
            ))}
          </div>
          <div className="cp-grid-3" style={{ marginTop: 24 }}>
            {PROD_PATTERNS.slice(3, 6).map(({ Icon, title, text }) => (
              <div className="cp-card" key={title}>
                <div className="cp-card-icon"><Icon size={22} /></div>
                <h3 className="cp-card-title">{title}</h3>
                <p className="cp-card-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work — reuse HowItWorks */}
      <section className="cp-section">
        <div className="cp-container">
          <div style={{ maxWidth: 720, marginBottom: 48 }}>
            <span className="cp-eyebrow">How we work</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>Predictable process. Fixed fee. Real demos every week.</h2>
            <p className="cp-lede">
              No open-ended scopes, no hourly billing surprises. You see progress weekly,
              and we don't kick off the build until we've both agreed on what "done" means.
            </p>
          </div>
          <HowItWorks steps={SHIP_STEPS} />
        </div>
      </section>

      {/* Pricing model */}
      <section className="cp-section cp-section--gradient">
        <div className="cp-container" style={{ maxWidth: 880 }}>
          <span className="cp-eyebrow">Pricing model</span>
          <h2 className="cp-h2" style={{ marginTop: 16, color: '#fff' }}>
            Two pieces. Transparent. No surprises.
          </h2>
          <p className="cp-lede" style={{ marginBottom: 32 }}>
            We share a fixed-fee proposal after a paid discovery, so the price you see is
            the price you pay. Maintenance and platform are billed monthly — predictable
            and stoppable.
          </p>

          <div className="cp-grid-2">
            <div className="cp-card cp-card--ink">
              <div className="cp-card-icon"><Rocket size={22} /></div>
              <h3 className="cp-card-title">One-time implementation</h3>
              <p className="cp-card-text">
                Fixed fee for design, build, and launch. Set after the discovery — never
                changes mid-project. Includes data migration, soft launch, and handoff
                documentation.
              </p>
            </div>
            <div className="cp-card cp-card--ink">
              <div className="cp-card-icon"><LifeBuoy size={22} /></div>
              <h3 className="cp-card-title">Monthly platform + support</h3>
              <p className="cp-card-text">
                CoPlaid hosting, auth, and infrastructure included. Pick a support tier
                that matches your needs — from light maintenance to full Run mode where we
                operate the app for you.
              </p>
            </div>
          </div>

          <div style={{
            marginTop: 32, padding: 'var(--sp-6) var(--sp-8)',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-md)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 'var(--sp-6)', flexWrap: 'wrap',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.78)' }}>
              <strong style={{ color: '#fff' }}>Not sure if it's a fit?</strong>{' '}
              Send us your prototype or a paragraph about your idea — we'll tell you in 48 hours.
            </div>
            <button className="cp-btn cp-btn--primary" onClick={() => navigate('/contact')}>
              Send us your idea <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cp-section cp-section--surface">
        <div className="cp-container" style={{ maxWidth: 860 }}>
          <div style={{ marginBottom: 32 }}>
            <span className="cp-eyebrow">FAQ</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>Common questions.</h2>
          </div>
          <Faq items={FAQS} />
        </div>
      </section>

      {/* CTA */}
      <section className="cp-section">
        <div className="cp-container">
          <CtaBand
            title="Got a prototype? Let's make it real."
            sub="Send us your vibe-coded app, your spec, or just a paragraph about your idea. We'll tell you what shipping it would look like."
            primary={{ label: 'Book a build call', to: '/contact' }}
            secondary={{ label: 'Email us your idea', to: '/contact' }}
          />
        </div>
      </section>
    </div>
  );
};

export default Ship;
